













































import { useVuelidate } from '@vuelidate/core';
import { helpers, maxLength, required } from '@vuelidate/validators';

export default {
    name: 'ListedJobInviteForm',

    props: {
        job_application_uuid: {
            required: false,
            type: String,
        },

        applicant_uuid: {
            required: true,
            type: String,
        },

        applicant_name: {
            required: true,
            type: String,
        },
    },

    setup(props) {
        let error = ref<string | null>(null);
        let loading = ref<boolean>(false);

        const initialFormData = () => ({
            applicant_uuid: props.applicant_uuid,
            job: null,
            note: null,
        });

        const form = reactive<object>(initialFormData());

        const rules = computed(() => ({
            job: {
                required: helpers.withMessage('The job field is required.', required),
                $lazy: true,
            },
            note: {
                required: helpers.withMessage('The message field is required.', required),
                maxLength: helpers.withMessage('The note cannot exceed 500 characters.', maxLength(500)),
                $lazy: true,
            },
        }));

        const v$ = useVuelidate(rules, form);

        return {
            form,
            error,
            initialFormData,
            loading,
            v$,
        };
    },

    computed: {
        currentJob() {
            return this.$store.getters['jobs/single/getSingleJob'];
        },

        jobs() {
            const jobs = this.$store.getters['jobs/getDropdownJobs'];

            return this.job_application_uuid ? jobs?.filter((job) => job.uuid !== this.currentJob.uuid) : jobs;
        },

        messagePlaceholder() {
            return `Hi ${this.applicant_name}, I enjoyed reading your profile and would love to chat to you about what we have to offer.`;
        },

        invitationMessage() {
            const linkToJob = `${process.env.CANDIDATE_APP_URL}/jobs/${this.form.job.uuid}`;

            let message = `<b>New Job Invitation:</b>  ${this.form.job.title}<br/><br/>`;
            message += `${this.form.note}<br/><br/>`;
            message += `<a class="underline" target="_blank" href="${linkToJob}">Please click here for more information.</a>`;

            const data = new FormData();
            data.append('body', message);
            return data;
        },
    },

    created() {
        this.getJobs();
        this.getJobApplication();
    },

    mounted() {
        this.form.note = this.messagePlaceholder;
    },

    methods: {
        closeDialog() {
            this.$emit('close-dialog');
        },

        getJobs() {
            this.$store.dispatch('jobs/getDropdownJobs');
        },

        async getJobApplication() {
            if (!this.job_application_uuid) return;

            await this.$store.dispatch('applications/getJobApplication', this.job_application_uuid);
        },

        async sendInvitation() {
            await this.v$.$validate();

            if (this.v$.$invalid) return;

            this.loading = true;

            this.$store
                .dispatch('jobs/inviteToJob', {
                    job_uuid: this.form.job.uuid,
                    applicant_uuid: this.applicant_uuid,
                    note: this.form.note,
                })
                .then(() => {
                    this.initiateConversation();
                })
                .catch((err) => {
                    this.error = err.response.data.message;
                    this.loading = false;
                });
        },

        async initiateConversation() {
            this.$store
                .dispatch('conversations/createConversation', {
                    job_uuid: this.form.job.uuid,
                    candidate_uuid: this.applicant_uuid,
                    initiated_from_job_invite: 1,
                })
                .then((response) => {
                    this.sendMessage(response.uuid);
                })
                .catch((error) => {
                    this.error = error.response.data.message;
                    this.loading = false;
                });
        },

        async sendMessage(conversation_uuid) {
            await this.$store
                .dispatch('conversations/sendMessage', {
                    conversation_uuid: conversation_uuid,
                    data: this.invitationMessage,
                })
                .then((response) => {
                    this.resetForm();

                    this.closeDialog();

                    this.$toast.show({
                        type: 'success',
                        message: 'Job invitation sent.',
                    });
                })
                .catch((error) => {
                    this.error = error.response.data.message;
                    this.loading = false;
                });
        },

        resetForm() {
            this.v$.$reset();
            this.form = this.initialFormData();
            this.error = null;
            this.loading = false;
        },

        showUnlistedJobForm() {
            this.resetForm();

            this.$emit('show-unlisted-job-form');
        },
    },
};
